namespace Solovis.Reports.Parts.Tables.DataTableHelpers.CellEditors {

  function parseNumericInput(numberString: string): number | null {
    if (numberString === null || typeof numberString === "undefined") {
      return null;
    }
    const trimmedNumber = numberString.trim();
    if (trimmedNumber.length === 0) {
      return null;
    }
    const cleanedNumber = InvData.DecimalHelper.cleanNumberInputString(trimmedNumber);
    const parsed = InvData.DecimalHelper.decimalStringAsNumber(cleanedNumber, false);
    if (isNaN(parsed)) {
      return null;
    }
    return parsed;
  }

  /**
   * Generic numeric editor. Suffix is rendered after the input (e.g. "%" for percentages).
   * `displayScale` lets percentage editors multiply by 100 on display and divide on commit
   * — percentages are stored as decimal fractions in this codebase (0.05 = 5%).
   */
  function makeNumericEditor(options: {
    suffix?: string;
    prefix?: string;
    displayScale?: number;
    integerOnly?: boolean;
  }): CellEditorFactory {
    const displayScale = options.displayScale ?? 1;

    return function (initialValue, ctx, onCommit): CellEditor {
      // Flex container fills the td so the input expands with column resize and a fixed
      // prefix/suffix (e.g. "%") sits flush against the input.
      const wrapper = document.createElement("span");
      wrapper.className = "data-table-cell-editor numeric-cell-editor";
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
      wrapper.style.width = "100%";
      wrapper.style.boxSizing = "border-box";

      if (options.prefix) {
        const prefixEl = document.createElement("span");
        prefixEl.className = "cell-editor-prefix";
        prefixEl.textContent = options.prefix;
        prefixEl.style.flex = "0 0 auto";
        wrapper.appendChild(prefixEl);
      }

      const input = document.createElement("input");
      input.type = "text";
      input.className = "cell-editor-input";
      input.style.textAlign = "right";
      input.style.flex = "1 1 auto";
      input.style.minWidth = "0";
      input.style.margin = "0";
      input.style.boxSizing = "border-box";
      wrapper.appendChild(input);

      if (options.suffix) {
        const suffixEl = document.createElement("span");
        suffixEl.className = "cell-editor-suffix";
        suffixEl.textContent = options.suffix;
        suffixEl.style.flex = "0 0 auto";
        wrapper.appendChild(suffixEl);
      }

      const toDisplay = (storedValue: any): string => {
        if (storedValue === null || storedValue === undefined || storedValue === "") {
          return "";
        }
        const scaled = (typeof storedValue === "number" ? storedValue : Number(storedValue)) * displayScale;
        if (isNaN(scaled)) {
          return "";
        }
        return InvData.DecimalHelper.fixupThousandsSeparatorAsNumberString(scaled) ?? "";
      };

      let committedValue = initialValue;
      input.value = toDisplay(initialValue);

      // On focus, swap a placeholder zero for a blank field to make over-typing easier.
      input.addEventListener("focus", () => {
        input.value = InvData.DecimalHelper.preProcessNumericInputBeforeEdit(input.value) ?? "";
        input.select();
      });

      const commit = () => {
        let newValue = parseNumericInput(input.value);
        if (newValue !== null && displayScale !== 1) {
          newValue = newValue / displayScale;
        }
        if (newValue !== null && options.integerOnly) {
          newValue = Math.trunc(newValue);
        }

        // Refresh display formatting either way (e.g. user typed "1000" -> show "1,000").
        input.value = toDisplay(newValue);

        if ((newValue ?? 0) !== (committedValue ?? 0)) {
          committedValue = newValue;
          onCommit(newValue);
        }
      };

      input.addEventListener("blur", commit);
      input.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          input.blur();
        }
        else if (e.key === "Escape") {
          e.preventDefault();
          input.value = toDisplay(committedValue);
          input.blur();
        }
      });

      return {
        element: wrapper,
        getValue: () => committedValue,
        setValue: (v) => {
          committedValue = v;
          input.value = toDisplay(v);
        },
        destroy: () => {
          // Nothing to clean up — listeners are bound to the DOM node which is being detached.
        }
      };
    };
  }

  function makeBooleanEditor(): CellEditorFactory {
    return function (initialValue, ctx, onCommit): CellEditor {
      const input = document.createElement("input");
      input.type = "checkbox";
      input.className = "data-table-cell-editor boolean-cell-editor";
      input.checked = !!initialValue;

      let committedValue = !!initialValue;

      input.addEventListener("change", () => {
        const newValue = input.checked;
        if (newValue !== committedValue) {
          committedValue = newValue;
          onCommit(newValue);
        }
      });

      return {
        element: input,
        getValue: () => committedValue,
        setValue: (v) => {
          committedValue = !!v;
          input.checked = committedValue;
        },
        destroy: () => { /* no-op */ }
      };
    };
  }

  function makeTextEditor(): CellEditorFactory {
    return function (initialValue, ctx, onCommit): CellEditor {
      const input = document.createElement("input");
      input.type = "text";
      input.className = "data-table-cell-editor text-cell-editor";
      input.style.width = "100%";
      input.style.margin = "0";
      input.style.boxSizing = "border-box";
      input.value = initialValue ?? "";

      let committedValue = initialValue ?? "";

      const commit = () => {
        const newValue = input.value;
        if (newValue !== committedValue) {
          committedValue = newValue;
          onCommit(newValue);
        }
      };

      input.addEventListener("blur", commit);
      input.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          input.blur();
        }
        else if (e.key === "Escape") {
          e.preventDefault();
          input.value = committedValue ?? "";
          input.blur();
        }
      });

      return {
        element: input,
        getValue: () => committedValue,
        setValue: (v) => {
          committedValue = v ?? "";
          input.value = committedValue;
        },
        destroy: () => { /* no-op */ }
      };
    };
  }

  let _builtInsRegistered = false;

  /**
   * Registers all built-in editors. Idempotent. Called by DataTableControl on first init.
   * Reports that want to override a built-in can call `registerEditor` after this runs.
   */
  export function registerBuiltInEditors(): void {
    if (_builtInsRegistered) {
      return;
    }
    _builtInsRegistered = true;

    registerEditor("number", makeNumericEditor({}));
    registerEditor("integer", makeNumericEditor({ integerOnly: true }));
    registerEditor("currency", makeNumericEditor({}));
    registerEditor("percentage", makeNumericEditor({ suffix: "%", displayScale: 100 }));
    registerEditor("boolean", makeBooleanEditor());
    registerEditor("text", makeTextEditor());
  }
}
